import { getDeviceId, getSessionId } from '@amplitude/analytics-browser';
import capitalize from 'lodash/capitalize';
import { useRouter, useSearchParams, useSelectedLayoutSegments } from 'next/navigation';
import qs from 'query-string';
import { useCallback, useRef } from 'react';

import { useAuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useIsAuthenticating } from '@/context/auth/IsAuthenticatingProvider';
import { useAnalytics } from '@/hooks/analytics';
import { useApi } from '@/hooks/api';
import { useSetupClassicUserAfterAuth as useSetupUserAfterAuth } from '@/hooks/forms/useSetupUserAfterAuth';
import { useGuestPassRequestCode } from '@/hooks/guestPass/useGuestPassRequestCode';
import { usePartnerState, usePurchaseParamsState, useUserState } from '@/hooks/store';
import type { User } from '@/store/user/types';
import { getCouponForLogging, getSlug } from '@/utils/analytics/setupEventProps';
import { AUTH_RETURN_PATH_PARAM } from '@/utils/authReturnTo';
import getUrl from '@/utils/getUrl';
import { connectionByAuthMethod } from '@/utils/privacyCookies';

import { BroadcastChannelEvent, BroadcastChannelName } from './enums';
import type { SignupArgs, UseAuth } from './types';

export default function useClassicAuth(): UseAuth {
	const searchParams = useSearchParams();
	const user = useUserState();

	const apiRequest = useApi();

	const setupUserAfterAuth = useSetupUserAfterAuth();
	const { setIsAuthenticating } = useIsAuthenticating();
	const { authPrompt } = useAuthPrompt();
	const signupPopup = useRef<Window | null>(null);
	const { logEvent } = useAnalytics();
	const guestPassCode = useGuestPassRequestCode();
	const partner = usePartnerState();
	const purchaseParamsState = usePurchaseParamsState();
	const router = useRouter();
	const isAppRouter = useSelectedLayoutSegments() !== null;

	const authenticate = useCallback(
		({ authMethod, marketingOptIn, onAuthSuccess, loginHint }: SignupArgs) => {
			logEvent({
				eventName: `Login Form : ${capitalize(authMethod)} : Clicked`,
				eventProps: {
					mode: authPrompt,
				},
			});
			setIsAuthenticating(true);

			const channel = new BroadcastChannel(BroadcastChannelName.TAB_COMMUNICATION);
			channel.onmessage = async function (event) {
				const { type } = event.data;

				if (
					type === BroadcastChannelEvent.LOGIN_COMPLETED ||
					type === BroadcastChannelEvent.SIGNUP_COMPLETED
				) {
					// Close channel, so that we avoid catching any subsequent events (WINDOW_CLOSED in particular).
					channel.close();

					try {
						const { data } = await apiRequest<User>({
							endpoint: 'v2/me',
							method: 'GET',
						});
						await setupUserAfterAuth(data, authMethod);
						const authSuccess = onAuthSuccess?.(data);
						if (isAppRouter) {
							router.refresh();
						}
						logEvent({
							eventName: `Login Form : ${capitalize(authMethod)} : Success`,
							eventProps: {
								mode: type === BroadcastChannelEvent.LOGIN_COMPLETED ? 'login' : 'signup',
							},
						});
						if (type === BroadcastChannelEvent.SIGNUP_COMPLETED) {
							logEvent({
								eventName: 'Account : User : Created',
								eventProps: {
									...(!!data?.id && { calm_user_id: data.id }),
								},
							});
						}
						return authSuccess;
					} catch (error) {
						logEvent({
							eventName: `Login Form : ${capitalize(authMethod)} : Error`,
							eventProps: {
								mode: type === BroadcastChannelEvent.LOGIN_COMPLETED ? 'login' : 'signup',
							},
						});
					} finally {
						setIsAuthenticating(false);
					}
				}

				if (type === BroadcastChannelEvent.WINDOW_CLOSED) {
					logEvent({
						eventName: `Login Form : ${capitalize(authMethod)} : Cancelled`,
						eventProps: {
							mode: authPrompt,
						},
					});
					channel.close();
					setIsAuthenticating(false);
				}
			};

			if (signupPopup.current) {
				signupPopup.current.focus();
			} else {
				const left = (window.innerWidth - 600) / 2 + window.screenX;
				const top = (window.innerHeight - 600) / 2 + window.screenY;
				// Read from window.location: the landings strip params with history.replaceState, which searchParams may not reflect.
				let returnSearch = window.location.search;
				// Partner landings sign the visitor out on load unless preserveSession is set, which would end the session this login creates.
				if (partner) {
					const returnParams = new URLSearchParams(window.location.search);
					returnParams.set('preserveSession', 'true');
					returnSearch = `?${returnParams.toString()}`;
				}
				signupPopup.current = window.open(
					`${getUrl('www')}/auth/login?${qs.stringify({
						connection: connectionByAuthMethod[authMethod],
						screen_hint: authPrompt,

						marketing_opt_in: marketingOptIn,
						guest_pass_code: guestPassCode,
						partner_id: partner?.id,
						partner_type: [partner?.category?.toLowerCase() || null],
						slug: getSlug(),
						coupon: getCouponForLogging(purchaseParamsState?.coupon),
						login_hint: loginHint,
						onboard_id: searchParams?.get('onboardId') ?? undefined,
						[AUTH_RETURN_PATH_PARAM]: `${window.location.pathname}${returnSearch}`,
						// send the device id to universal login page to connect the device with the user
						['ext-amp_device_id']: getDeviceId(),
						['ext-amp_session_id']: getSessionId(),
					})}`,
					'_blank',
					`popup,width=600,height=600,top=${top},left=${left}`,
				);

				if (signupPopup.current) {
					const interval = setInterval(() => {
						if (signupPopup.current?.closed) {
							signupPopup.current = null;
							clearInterval(interval);
							const channel = new BroadcastChannel(BroadcastChannelName.TAB_COMMUNICATION);
							channel.postMessage({ type: BroadcastChannelEvent.WINDOW_CLOSED });
						}
					}, 500);
				}
			}
		},
		[
			apiRequest,
			authPrompt,
			guestPassCode,
			isAppRouter,
			logEvent,
			partner,
			purchaseParamsState?.coupon,
			router,
			setIsAuthenticating,
			setupUserAfterAuth,
			searchParams,
		],
	);

	return { authenticate, isLoading: false, isAuth0User: user?.current_auth_type === 'auth0' };
}
